import { useEffect, useMemo, useState } from "react";
import { t } from "@/i18n-js/instance";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { RadioButtonGroup } from "@circle-react-shared/RadioButtonGroup";
import { Typography } from "@circle-react-uikit/Typography";
import { useEventsContext } from "../../EditEventContext";
import { RECURRING_EDIT_MODE } from "../../utils";

const translationRoot = "events.edit.recurring_event_modal";

interface EditRecurringModalProps {
  defaultSelection?: string;
  isDraft?: boolean;
  isPublishing?: boolean;
  onCloseModal: () => void;
  onConfirm: (frequency: string) => void;
  shouldHideAllEventOption?: boolean;
  show?: boolean;
  showCurrentAndRemainingOnly?: boolean;
  showCurrentOptionOnly?: boolean;
  showRemainingOptionOnly?: boolean;
}

interface RadioOption {
  label: string;
  value: string;
}

export const EditRecurringModal = ({
  show = false,
  onConfirm,
  onCloseModal,
  isPublishing = false,
  showCurrentOptionOnly,
  showRemainingOptionOnly,
  showCurrentAndRemainingOnly,
  shouldHideAllEventOption,
  defaultSelection,
  isDraft,
}: EditRecurringModalProps) => {
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    defaultSelection || RECURRING_EDIT_MODE.CURRENT,
  );
  const { isEmailNotificationsEnabled, isAutoRsvpInvitedAttendees } =
    useEventsContext();

  // Update selected frequency when defaultSelection changes
  useEffect(() => {
    setSelectedFrequency(defaultSelection || RECURRING_EDIT_MODE.CURRENT);
  }, [defaultSelection]);

  const onRadioChangeHandler = (value: string): void => {
    setSelectedFrequency(value);
  };

  const onConfirmHandler = (): void => {
    onConfirm(selectedFrequency);
  };

  const defaultOptions: RadioOption[] = [
    {
      label: t(`${translationRoot}.this_event`),
      value: RECURRING_EDIT_MODE.CURRENT,
    },
    {
      label: t(`${translationRoot}.this_and_following`),
      value: RECURRING_EDIT_MODE.REMAINING,
    },
    {
      label: t(`${translationRoot}.all_events`),
      value: RECURRING_EDIT_MODE.ALL,
    },
  ];

  const areMultipleEvents =
    selectedFrequency === RECURRING_EDIT_MODE.ALL ||
    selectedFrequency === RECURRING_EDIT_MODE.REMAINING;

  const menuOptions = useMemo<RadioOption[]>(() => {
    if (showCurrentOptionOnly) {
      return defaultOptions.filter(
        option => option.value === RECURRING_EDIT_MODE.CURRENT,
      );
    }

    if (showRemainingOptionOnly) {
      return defaultOptions.filter(
        option => option.value === RECURRING_EDIT_MODE.REMAINING,
      );
    }

    if (showCurrentAndRemainingOnly) {
      return defaultOptions.filter(
        option =>
          option.value === RECURRING_EDIT_MODE.CURRENT ||
          option.value === RECURRING_EDIT_MODE.REMAINING,
      );
    }

    if (shouldHideAllEventOption) {
      return defaultOptions.filter(
        option => option.value !== RECURRING_EDIT_MODE.ALL,
      );
    }

    return defaultOptions;
  }, [
    showCurrentOptionOnly,
    showRemainingOptionOnly,
    showCurrentAndRemainingOnly,
    shouldHideAllEventOption,
  ]);

  const shouldShowEmailNotificationsMessage =
    isEmailNotificationsEnabled && areMultipleEvents && isPublishing;

  const shouldShowUpdateEventNotificationsMessage =
    !isDraft &&
    isEmailNotificationsEnabled &&
    (showCurrentAndRemainingOnly || showRemainingOptionOnly) &&
    selectedFrequency === RECURRING_EDIT_MODE.REMAINING;

  const shouldShowKeepOriginalSeriesNotice =
    selectedFrequency === RECURRING_EDIT_MODE.CURRENT &&
    (showCurrentOptionOnly || showCurrentAndRemainingOnly);

  return (
    <ConfirmationModal
      title={
        isPublishing
          ? t(`${translationRoot}.title_publish`)
          : t(`${translationRoot}.title`)
      }
      isOpen={show}
      confirmText={t(`${translationRoot}.accept`)}
      cancelText={t(`${translationRoot}.cancel`)}
      onClose={onCloseModal}
      onCancel={onCloseModal}
      onConfirm={onConfirmHandler}
      size="md"
    >
      <div className="mt-6">
        <RadioButtonGroup
          defaultChecked={selectedFrequency}
          name="recurring_event_settings.frequency_edit"
          options={menuOptions}
          size="sm"
          onChange={onRadioChangeHandler}
        />
      </div>
      {shouldShowEmailNotificationsMessage && (
        <div className="mt-4 leading-4">
          <Typography.LabelXs color="text-light" weight="semibold" as="span">
            {isAutoRsvpInvitedAttendees
              ? t(`${translationRoot}.confirmation_email_notifications`)
              : t(`${translationRoot}.invitation_email_notifications`)}
          </Typography.LabelXs>
        </div>
      )}
      {shouldShowKeepOriginalSeriesNotice && (
        <div className="mt-4 leading-4">
          <Typography.LabelXs color="text-light" weight="semibold" as="span">
            {t(`${translationRoot}.keep_original_series_notice`)}
          </Typography.LabelXs>
        </div>
      )}
      {shouldShowUpdateEventNotificationsMessage && (
        <div className="mt-4 leading-4">
          <Typography.LabelXs color="text-light" weight="semibold" as="span">
            {t(`${translationRoot}.update_event_notifications`)}
          </Typography.LabelXs>
        </div>
      )}
    </ConfirmationModal>
  );
};
